import React, { useEffect, useRef, useState } from 'react';
import FormField from './FormField';
import QRPreview from './QRPreview';
import ImageCapture from './ImageCapture';
import { saveLogisticsRecord } from '../utils/supabase';

const LogisticsForm = ({ 
  sku, 
  setSku, 
  serialNumbers, 
  setSerialNumbers, 
  labelSizes, 
  setLabelSizes,
  qrCodeDataUrl,
  generateQrCode
}) => {
  const serialNumbersRef = useRef(null);
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Focus handler after SKU scan
  useEffect(() => {
    const skuInput = document.getElementById('sku-input');
    
    const handleSkuKeyPress = (e) => {
      if (e.key === 'Enter' && sku.trim() !== '') {
        e.preventDefault();
        if (serialNumbersRef.current) {
          serialNumbersRef.current.focus();
        }
      }
    };
    
    if (skuInput) {
      skuInput.addEventListener('keypress', handleSkuKeyPress);
    }
    
    return () => {
      if (skuInput) {
        skuInput.removeEventListener('keypress', handleSkuKeyPress);
      }
    };
  }, [sku]);

  const handleSerialNumbersChange = (e) => {
    setSerialNumbers(e.target.value);
  };

  const handleLabelSizeChange = (size) => {
    setLabelSizes(prev => ({
      ...prev,
      [size]: !prev[size]
    }));
  };

  const handleImageCapture = (capturedImages) => {
    setImages(capturedImages);
  };

  const handleGenerateAndSave = async () => {
    if (!sku.trim()) return;
    
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    
    try {
      // First generate QR code
      generateQrCode();
      
      // Convert image data URLs to blobs with better error handling
      const imageBlobs = await Promise.all(images.map(async (imageData, index) => {
        try {
          // Handle both string URLs and object format
          const url = typeof imageData === 'string' ? imageData : imageData.url;
          const name = typeof imageData === 'string' ? `logistics-${Date.now()}-${index}.jpg` : (imageData.name || `logistics-${Date.now()}-${index}.jpg`);
          const type = typeof imageData === 'string' ? 'image/jpeg' : (imageData.type || 'image/jpeg');
          
          // For data URLs, convert directly without fetch
          if (url.startsWith('data:')) {
            const arr = url.split(',');
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while(n--) {
              u8arr[n] = bstr.charCodeAt(n);
            }
            return new File([u8arr], name, { type: mime || type });
          } else {
            // For regular URLs, use fetch
            const response = await fetch(url);
            const blob = await response.blob();
            return new File([blob], name, { type: type });
          }
        } catch (error) {
          console.error('Error converting image:', error, imageData);
          throw error;
        }
      }));
      
      // Parse serial numbers
      const serialNumbersArray = serialNumbers
        .split('\n')
        .map(s => s.trim())
        .filter(s => s.length > 0);
      
      // Save record
      await saveLogisticsRecord({
        sku,
        serialNumbers: serialNumbersArray,
        metadata: { labelSizes }
      }, imageBlobs);
      
      setSaveSuccess(true);
      
      // Clear form after successful save
      setTimeout(() => {
        setSku('');
        setSerialNumbers('');
        setImages([]);
        setSaveSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Error saving record:', error);
      setSaveError(error.message || 'Error al guardar el registro');
    } finally {
      setSaving(false);
    }
  };

  // Generate QR when data changes (real-time preview)
  useEffect(() => {
    if (sku.trim() !== '') {
      const timer = setTimeout(() => {
        generateQrCode();
      }, 500); // Small delay to prevent excessive re-renders
      
      return () => clearTimeout(timer);
    }
  }, [sku, serialNumbers, labelSizes, generateQrCode]);

  return (
    <div className="logistics-form active">
      <div className="form-container">
        <FormField
          label="SKU:"
          id="sku-input"
          type="text"
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          required={true}
          placeholder="Escanea o ingresa el SKU"
          autoFocus={true}
          infoTooltip="Código de producto"
        />

        <FormField
          label="Números de Serie (uno por línea):"
          id="serial-numbers"
          type="textarea"
          value={serialNumbers}
          onChange={handleSerialNumbersChange}
          placeholder="Ingresa un número de serie por línea"
          required={false}
          infoTooltip="Ingresa cada número de serie en una línea separada"
        />

        <div className="form-group">
          <label>Tamaño de Etiqueta:</label>
          <div className="checkbox-group">
            <div className="checkbox-option">
              <input
                type="checkbox"
                id="label-size-large"
                checked={labelSizes.large || false}
                onChange={() => handleLabelSizeChange('large')}
              />
              <label htmlFor="label-size-large">110mm (alto) x 50mm (ancho)</label>
            </div>
            <div className="checkbox-option">
              <input
                type="checkbox"
                id="label-size-small"
                checked={labelSizes.small || false}
                onChange={() => handleLabelSizeChange('small')}
              />
              <label htmlFor="label-size-small">57mm (alto) x 32mm (ancho)</label>
            </div>
          </div>
        </div>

        <ImageCapture
          onImageCapture={handleImageCapture}
          maxImages={5}
          label="Fotos del Producto (opcional)"
        />

        <div className="button-group">
          <button 
            className="button save-button" 
            onClick={handleGenerateAndSave}
            disabled={!sku.trim() || saving}
          >
            {saving ? 'Procesando...' : 'Generar QR y Guardar Registro'}
          </button>
        </div>

        {saveSuccess && (
          <div className="success-message">
            ✅ Registro guardado exitosamente
          </div>
        )}
        
        {saveError && (
          <div className="error-message">
            ❌ Error: {saveError}
          </div>
        )}
      </div>

      {qrCodeDataUrl && (
        <QRPreview 
          qrCodeDataUrl={qrCodeDataUrl} 
          qrData={`SKU: ${sku}\nSeriales: ${serialNumbers}`}
          labelSize={labelSizes.large ? 'large' : labelSizes.small ? 'small' : ''}
        />
      )}
    </div>
  );
};

export default LogisticsForm;